import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Printer } from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import SearchableSelect from '@/components/shared/SearchableSelect';
import PersianDatePickerFa from '@/components/shared/PersianDatePickerFa';
import {
  readChequeBooks,
  readRegisteredCheques,
  writeRegisteredCheques,
  leafRangeInclusive,
  usedLeafNumbers,
  type ChequeBookRow,
  type RegisteredChequeRow,
} from '@/lib/chequeBooksStorage';
import { amountToPersianWordsRial } from '@/lib/rialWordsFa';
import { getChequeVisualSettings } from '@/lib/chequeVisualSettings';

function toFaDigits(value: string | number): string {
  return String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
}

function formatThousandsDigits(digits: string): string {
  if (!digits) return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function digitsOnly(s: string, maxLen = 15): string {
  return s.replace(/\D/g, '').slice(0, maxLen);
}

const datePickerChequeClass =
  'w-full h-8 rounded-none border-0 border-b border-slate-700 bg-transparent px-0 py-1 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 sm:h-9';

const ChequeRegisterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const preBookId = searchParams.get('book')?.trim() ?? '';

  const chequeVisual = getChequeVisualSettings();

  const [books, setBooks] = React.useState<ChequeBookRow[]>(() => readChequeBooks());
  const [registeredCheques, setRegisteredCheques] = React.useState<RegisteredChequeRow[]>(() =>
    readRegisteredCheques(),
  );
  const [chequeBookId, setChequeBookId] = React.useState('');
  const [leafNumber, setLeafNumber] = React.useState('');
  const [fullName, setFullName] = React.useState('');
  const [nationalCode, setNationalCode] = React.useState('');
  const [amountDigits, setAmountDigits] = React.useState('');
  const [dueDate, setDueDate] = React.useState('');
  const [amountInWords, setAmountInWords] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [chequeError, setChequeError] = React.useState('');
  const [chequeMessage, setChequeMessage] = React.useState('');
  const [lastPrintId, setLastPrintId] = React.useState<string | null>(null);

  React.useEffect(() => {
    setBooks(readChequeBooks());
    setRegisteredCheques(readRegisteredCheques());
  }, []);

  React.useEffect(() => {
    if (!preBookId) return;
    const exists = books.some((b) => b.id === preBookId);
    if (exists) setChequeBookId(preBookId);
  }, [preBookId, books]);

  React.useEffect(() => {
    const n = Number(amountDigits);
    if (!amountDigits || !Number.isFinite(n) || n <= 0) {
      setAmountInWords('');
      return;
    }
    setAmountInWords(amountToPersianWordsRial(n));
  }, [amountDigits]);

  const selectedBook = books.find((b) => b.id === chequeBookId) ?? null;
  const fromN = selectedBook ? Number(selectedBook.fromLeaf) : NaN;
  const toN = selectedBook ? Number(selectedBook.toLeaf) : NaN;
  const used = selectedBook ? usedLeafNumbers(selectedBook.id, registeredCheques) : new Set<number>();
  const leafOptions = React.useMemo(() => {
    if (!selectedBook || !Number.isFinite(fromN) || !Number.isFinite(toN)) return [];
    return leafRangeInclusive(fromN, toN)
      .filter((n) => !used.has(n))
      .map((n) => ({ value: String(n), label: `برگ ${toFaDigits(n)} (${n})` }));
  }, [selectedBook, fromN, toN, used]);

  const bookSelectOptions = books.map((b) => ({
    value: b.id,
    label: `${b.bankAccountLabel} — سری ${b.serie} (${b.fromLeaf}–${b.toLeaf})`,
  }));

  const amountNum = Number(amountDigits);
  const displayAmountBelow =
    Number.isFinite(amountNum) && amountNum > 0 ? formatThousandsDigits(String(amountNum)) : '…………………';

  const submitCheque = (e: React.FormEvent) => {
    e.preventDefault();
    setChequeError('');
    setChequeMessage('');
    setLastPrintId(null);
    if (!selectedBook) {
      setChequeError('ابتدا دسته‌چک را انتخاب کنید.');
      return;
    }
    const leaf = Number(leafNumber);
    if (!leafNumber.trim() || !Number.isFinite(leaf) || leaf <= 0) {
      setChequeError('شماره برگ را از لیست انتخاب کنید.');
      return;
    }
    if (leaf < fromN || leaf > toN) {
      setChequeError('شماره برگ خارج از بازهٔ این دسته است.');
      return;
    }
    if (used.has(leaf)) {
      setChequeError('این شماره برگ برای همین دسته قبلاً ثبت شده است.');
      return;
    }
    const cleanName = fullName.trim();
    const cleanNational = nationalCode.replace(/\D/g, '').slice(0, 10);
    const nAmount = Number(amountDigits);
    if (!cleanName || cleanNational.length !== 10 || !dueDate.trim() || !Number.isFinite(nAmount) || nAmount <= 0) {
      setChequeError('نام و نام خانوادگی، کد ملی ۱۰ رقمی، مبلغ و تاریخ سررسید الزامی است.');
      return;
    }
    const words = amountToPersianWordsRial(nAmount);
    if (!words.trim()) {
      setChequeError('مبلغ معتبر نیست.');
      return;
    }

    const newId = `ch-${Date.now()}`;
    const next: RegisteredChequeRow[] = [
      {
        id: newId,
        chequeBookId: selectedBook.id,
        leafNumber: String(leaf),
        bankAccountLabel: selectedBook.bankAccountLabel,
        serie: selectedBook.serie,
        fullName: cleanName,
        nationalCode: cleanNational,
        amount: nAmount,
        dueDate: dueDate.trim(),
        amountInWords: words,
        description: description.trim(),
        status: 'pending',
        createdAt: new Date().toISOString(),
      },
      ...registeredCheques,
    ];
    setRegisteredCheques(next);
    writeRegisteredCheques(next);
    setLastPrintId(newId);
    setChequeMessage('چک با موفقیت ثبت شد. برای بایگانی می‌توانید نسخهٔ چاپی را باز کنید.');
    setLeafNumber('');
    setFullName('');
    setNationalCode('');
    setAmountDigits('');
    setDueDate('');
    setAmountInWords('');
    setDescription('');
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="ثبت برگه چک"
        description="دسته‌چک و شماره برگ از تعاریف قبلی؛ مبلغ به حروف از روی ریال به‌صورت خودکار پر می‌شود."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to="/accounting-settings/cheque">مهر و امضا</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/cheque-books">تعریف دسته‌چک</Link>
            </Button>
          </div>
        }
      />

      {books.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-slate-600">
            هنوز دسته‌چکی تعریف نشده است.{' '}
            <Link to="/cheque-books" className="font-medium text-violet-700 underline">
              از اینجا دسته‌چک را تعریف کنید
            </Link>
            .
          </CardContent>
        </Card>
      ) : (
        <form onSubmit={submitCheque} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">انتخاب دسته و شماره برگ</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-slate-600">دسته‌چک</label>
                <SearchableSelect
                  value={chequeBookId}
                  onChange={(id) => {
                    setChequeBookId(id);
                    setLeafNumber('');
                  }}
                  options={bookSelectOptions}
                  placeholder="انتخاب دسته‌چک"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-600">شماره برگ</label>
                <SearchableSelect
                  value={leafNumber}
                  onChange={setLeafNumber}
                  options={leafOptions}
                  placeholder={
                    selectedBook ? (leafOptions.length ? 'انتخاب شماره برگ' : 'همهٔ برگ‌ها ثبت شده‌اند') : 'ابتدا دسته را انتخاب کنید'
                  }
                  disabled={!selectedBook || leafOptions.length === 0}
                />
              </div>
            </CardContent>
          </Card>

          {chequeError ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{chequeError}</div>
          ) : null}
          {chequeMessage ? (
            <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              <p>{chequeMessage}</p>
              {lastPrintId ? (
                <Button asChild size="sm" variant="secondary" className="gap-1.5">
                  <Link to={`/cheque-books/print/${lastPrintId}`} target="_blank" rel="noopener noreferrer">
                    <Printer className="h-4 w-4" />
                    باز کردن صفحهٔ چاپ بایگانی
                  </Link>
                </Button>
              ) : null}
            </div>
          ) : null}

          <div className="mx-auto w-full max-w-4xl">
            <div className="rounded-xl border border-violet-200/90 bg-gradient-to-br from-violet-50 via-rose-50 to-indigo-100 p-3 shadow-md sm:p-4">
              <div
                dir="rtl"
                className="relative overflow-hidden rounded-sm border-[3px] border-slate-800 bg-gradient-to-br from-white/95 via-violet-50/35 to-rose-50/45 shadow-inner"
                style={{ aspectRatio: '2.15 / 1' }}
              >
                <div className="pointer-events-none absolute inset-0 border border-slate-600/35 m-1.5 rounded-sm" />
                <div className="relative flex h-full flex-col p-3 text-slate-900 sm:p-4 md:p-5">
                  <div className="flex flex-wrap items-start justify-between gap-2 border-b border-dashed border-slate-400 pb-2">
                    <div className="min-w-0 text-right">
                      <div className="text-[10px] text-slate-500 sm:text-xs">حساب / بانک</div>
                      <div className="text-xs font-bold leading-tight sm:text-sm md:text-base">
                        {selectedBook?.bankAccountLabel ?? '— — —'}
                      </div>
                      {selectedBook?.branchName ? (
                        <div className="mt-0.5 text-[10px] text-slate-600 sm:text-xs">شعبه: {selectedBook.branchName}</div>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1 text-left" dir="ltr">
                      <div className="flex items-center gap-2 font-mono text-xs text-slate-800 sm:text-sm">
                        <span className="text-slate-500">سری</span>
                        <span className="rounded border border-slate-400 bg-white px-2 py-0.5">{selectedBook?.serie ?? '—'}</span>
                      </div>
                      <div className="flex items-center gap-2 font-mono text-xs text-slate-800 sm:text-sm">
                        <span className="text-slate-500">شماره</span>
                        <span className="rounded border-2 border-slate-800 bg-white px-3 py-0.5 font-semibold tracking-widest">
                          {leafNumber ? toFaDigits(leafNumber) : '— — —'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 flex min-h-0 flex-1 flex-col gap-2 sm:gap-3">
                    <div className="space-y-1">
                      <span className="text-[10px] text-slate-600 sm:text-xs">در وجه آقای / خانم / شرکت</span>
                      <Input
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="h-8 border-0 border-b border-slate-700 bg-transparent px-0 text-sm font-medium shadow-none focus-visible:ring-0 sm:h-9 sm:text-base"
                        placeholder="نام کامل ذینفع"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4">
                      <div className="space-y-1">
                        <span className="text-[10px] text-slate-600 sm:text-xs">مبلغ به عدد (ریال)</span>
                        <Input
                          dir="ltr"
                          inputMode="numeric"
                          value={formatThousandsDigits(amountDigits)}
                          onChange={(e) => setAmountDigits(digitsOnly(e.target.value))}
                          className="h-8 border-0 border-b border-slate-700 bg-transparent px-0 font-mono text-sm shadow-none focus-visible:ring-0 sm:h-9"
                          placeholder="0"
                        />
                        <div dir="ltr" className="pt-0.5 text-end font-mono text-[11px] text-slate-700 sm:text-xs">
                          {displayAmountBelow}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <span className="text-[10px] text-slate-600 sm:text-xs">تاریخ سررسید</span>
                        <PersianDatePickerFa value={dueDate} onChange={setDueDate} inputClassName={datePickerChequeClass} />
                      </div>
                    </div>

                    <div className="min-h-0 flex-1 space-y-1">
                      <span className="text-[10px] text-slate-600 sm:text-xs">مبلغ به حروف (ریال — خودکار)</span>
                      <Textarea
                        readOnly
                        value={amountInWords}
                        rows={2}
                        className="min-h-[3rem] cursor-default resize-none border border-dashed border-slate-500 bg-white/85 text-sm leading-relaxed text-slate-800 shadow-none sm:min-h-[3.5rem] sm:text-base"
                        placeholder="با وارد کردن مبلغ عددی پر می‌شود"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-2 border-t border-dashed border-slate-400 pt-2 sm:grid-cols-12">
                      <div className="sm:col-span-4">
                        <span className="text-[10px] text-slate-600 sm:text-xs">شناسه / کد ملی ذینفع</span>
                        <Input
                          dir="ltr"
                          value={nationalCode}
                          onChange={(e) => setNationalCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          className="h-8 border-0 border-b border-slate-700 bg-transparent px-0 font-mono text-sm shadow-none focus-visible:ring-0 sm:h-9"
                          placeholder="۱۰ رقم"
                        />
                      </div>
                      <div className="sm:col-span-5">
                        <span className="text-[10px] text-slate-600 sm:text-xs">شرح (اختیاری)</span>
                        <Input
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          className="h-8 border-0 border-b border-slate-700 bg-transparent px-0 text-sm shadow-none focus-visible:ring-0 sm:h-9"
                        />
                      </div>
                      <div className="relative flex flex-col items-center justify-end sm:col-span-3">
                        <div className="mb-1 text-[10px] text-slate-500">مهر و امضا</div>
                        <div className="flex h-12 w-full max-w-[9rem] items-center justify-center gap-1">
                          {chequeVisual.sealDataUrl ? (
                            <img src={chequeVisual.sealDataUrl} alt="" className="max-h-11 max-w-[3.25rem] object-contain opacity-90" />
                          ) : null}
                          {chequeVisual.signatureDataUrl ? (
                            <img
                              src={chequeVisual.signatureDataUrl}
                              alt=""
                              className="max-h-11 max-w-[4.25rem] object-contain opacity-95"
                            />
                          ) : null}
                          {!chequeVisual.sealDataUrl && !chequeVisual.signatureDataUrl ? (
                            <div className="h-10 w-full rounded-md border border-dotted border-slate-400 bg-white/50" />
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-2 text-center text-xs text-slate-500">
              شمارهٔ روی برگه همان شمارهٔ برگ انتخابی است. پس از ثبت، از ستون «بایگانی» یا دکمهٔ بالا چاپ بگیرید.
            </p>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={!chequeBookId || !leafNumber}>
              ثبت این برگه
            </Button>
          </div>
        </form>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">چک‌های ثبت‌شده</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full min-w-[1020px] text-sm">
              <thead>
                <tr className="bg-slate-100 text-slate-700">
                  <th className="border px-2 py-2 text-right">حساب / دسته</th>
                  <th className="border px-2 py-2 text-right">سری</th>
                  <th className="border px-2 py-2 text-right">برگ</th>
                  <th className="border px-2 py-2 text-right">نام</th>
                  <th className="border px-2 py-2 text-right">کد ملی</th>
                  <th className="border px-2 py-2 text-right">مبلغ</th>
                  <th className="border px-2 py-2 text-right">سررسید</th>
                  <th className="border px-2 py-2 text-right">وضعیت</th>
                  <th className="border px-2 py-2 text-right">شرح</th>
                  <th className="border px-2 py-2 text-right">بایگانی</th>
                </tr>
              </thead>
              <tbody>
                {registeredCheques.map((c) => (
                  <tr key={c.id}>
                    <td className="max-w-[200px] truncate border px-2 py-2" title={c.bankAccountLabel}>
                      {c.bankAccountLabel}
                    </td>
                    <td className="border px-2 py-2 font-mono">{c.serie}</td>
                    <td className="border px-2 py-2 font-mono">{toFaDigits(c.leafNumber)}</td>
                    <td className="border px-2 py-2">{c.fullName}</td>
                    <td className="border px-2 py-2 font-mono">{c.nationalCode}</td>
                    <td className="border px-2 py-2 font-mono">{c.amount.toLocaleString('en-US')}</td>
                    <td className="border px-2 py-2">{c.dueDate}</td>
                    <td className="border px-2 py-2">
                      {c.status === 'pending' ? 'در انتظار' : c.status === 'cleared' ? 'پاس شده' : 'برگشتی'}
                    </td>
                    <td className="max-w-[160px] truncate border px-2 py-2">{c.description || '-'}</td>
                    <td className="border px-2 py-2">
                      <Button asChild size="sm" variant="outline" className="h-8 gap-1 text-xs">
                        <Link to={`/cheque-books/print/${c.id}`} target="_blank" rel="noopener noreferrer">
                          <Printer className="h-3.5 w-3.5" />
                          چاپ
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))}
                {registeredCheques.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="border px-2 py-4 text-center text-slate-500">
                      هنوز چکی ثبت نشده است.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChequeRegisterPage;
